import { Link } from 'react-router-dom';
import IndicateUp from '../common/cards/IndicateUp';
import { UnlockIcon, YouTubeIcon } from '../../svg';
import { ROUTES } from '../../routes/constants';
import { Button } from '../common/Button';
import { useContext } from 'react';
import { UserContext } from '../../context/UserContext';
import IndicateDown from '../common/cards/IndicateDown';
import millify from 'millify';
import useI18n from '../../hooks/useI18n';
import { useYouTubeStatisticsQuery } from '../../hooks/queries/useLinkQueries';

interface YouTubeAnalyticsProps {
  linkId: string;
  hasSubscription: boolean;
  isConnectedWithYoutube: boolean;
  interval: string;
}

const apiUrl = process.env.REACT_APP_API_URL;

export default function YoutubeAnalytics({
  hasSubscription,
  linkId,
  isConnectedWithYoutube,
  interval,
}: YouTubeAnalyticsProps) {
  const { t } = useI18n();
  const userContext = useContext(UserContext);
  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const { user } = userContext;

  const filter = {
    daily: 'day',
    weekly: 'week',
    monthly: 'month',
    yearly: 'year',
  } as const;

  const { data } = useYouTubeStatisticsQuery(
    user?._id ?? '',
    linkId,
    filter[interval as keyof typeof filter],
  );

  const handleConnect = () => {
    // Redirect to auth route for YouTube
    window.location.href = `${apiUrl}/auth/youtube`;
  };

  return (
    <div className="pt-4 pb-2 flex gap-2 lg:gap-5 lg:px-4 border-t border-t-[#E4E8EA] relative justify-between">
      {!hasSubscription ? (
        <div className="absolute top-[35px] right-0 bottom-[-14px] left-0 backdrop-blur-sm flex flex-col items-center md:justify-center z-10">
          <Link to={ROUTES.PLANS}>
            <Button
              variant="outlined"
              className="flex gap-2 items-center text-[#020D3A] border-[#020D3A]"
            >
              <UnlockIcon
                className="size-4 cursor-pointer select-none"
                stroke="#4D494F"
              />
              <span className="text-sm font-bold">
                {t('common.upgradeNow')}
              </span>
            </Button>
          </Link>
        </div>
      ) : (
        !isConnectedWithYoutube && (
          <div className="absolute top-[35px] right-0 bottom-[-14px] left-0 backdrop-blur-sm flex flex-col items-center md:justify-center z-10">
            <Link to={ROUTES.PLANS}>
              <Button
                variant="outlined"
                className="flex gap-2 items-center text-[#020D3A] border-[#020D3A]"
                onClick={handleConnect}
              >
                <YouTubeIcon className="size-5 cursor-pointer select-none" />
                <span className="text-sm font-bold">
                  {t('youtube.connectYouTube')}
                </span>
              </Button>
            </Link>
          </div>
        )
      )}
      <>
        <div className="flex flex-col ">
          <div>
            <span className="text-xs text-[#9B919D]">
              {t('youtube.newViews', 'New Views')}
            </span>
          </div>
          <div className="flex gap-2 items-center">
            <span className="text-md lg:text-2xl font-medium">
              {millify(data?.views ?? 0)}
            </span>
            {(data?.viewsGrowth ?? 0) >= 0 ? (
              <IndicateUp
                percent={millify(Math.abs(data?.viewsGrowth ?? 0))}
                className="!w-14 lg:!w-[65px]"
              />
            ) : (
              <IndicateDown
                percent={millify(Math.abs(data?.viewsGrowth ?? 0))}
                className="!w-14 lg:!w-[65px]"
              />
            )}
          </div>
        </div>
        {/* new likes */}
        <div className="flex flex-col">
          <div>
            <span className="text-xs text-[#9B919D]">
              {t('youtube.newLikes', 'New Likes')}
            </span>
          </div>
          <div className="flex gap-2 items-center">
            <span className="text-md lg:text-2xl font-medium">
              {millify(data?.likes ?? 0)}
            </span>
            {(data?.likesGrowth ?? 0) >= 0 ? (
              <IndicateUp
                percent={millify(Math.abs(data?.likesGrowth ?? 0))}
                className="!w-14 lg:!w-[65px]"
              />
            ) : (
              <IndicateDown
                percent={millify(Math.abs(data?.likesGrowth ?? 0))}
                className="!w-14 lg:!w-[65px]"
              />
            )}
          </div>
        </div>
        {/* New Subscribers */}
        <div className="flex flex-col">
          <div>
            <span className="text-xs text-[#9B919D]">
              {t('youtube.newSubscribers', 'New Subscribers')}
            </span>
          </div>
          <div className="flex gap-2 items-center">
            <span className="text-md lg:text-2xl font-medium">
              {millify(data?.subscribers ?? 0)}
            </span>
            {(data?.subscribersGrowth ?? 0) >= 0 ? (
              <IndicateUp
                percent={millify(Math.abs(data?.subscribersGrowth ?? 0))}
                className="!w-14 lg:!w-[65px]"
              />
            ) : (
              <IndicateDown
                percent={millify(Math.abs(data?.subscribersGrowth ?? 0))}
                className="!w-14 lg:!w-[65px]"
              />
            )}
          </div>
        </div>
      </>
    </div>
  );
}
